// Small in-page sub-navigation tab bar. Used to group related routes under one
// main-menu section (e.g. Ontology + Rules, Query + Browse) so the top nav stays
// uncluttered. Each tab routes to a full page; this just presents them as tabs.
//
// Tabs that name a `page` key consult the admin page-visibility config — the
// same source the Header nav and the PageGuard route backstop read. Sub-tabs
// used to publish their links unconditionally, which made the admin's browse/
// rules toggles inert (2026-08-06 audit D1).
import { NavLink } from 'react-router';
import { isPageMobile, isPageVisible, usePagesConfig } from '@/lib/usePages';
import { useIsMobile } from '@/lib/useIsMobile';
import { useMe } from '@/lib/useMe';

export interface SubTab {
  to: string;
  label: string;
  /** Admin page-visibility key; omit for tabs that are always on. */
  page?: string;
}

export function SubTabs({ tabs }: { tabs: SubTab[] }) {
  const pages = usePagesConfig();
  const role = useMe().data?.role;
  const isMobile = useIsMobile();
  const visible = tabs.filter(
    (t) =>
      !t.page ||
      (isPageVisible(pages.data, t.page, role) &&
        (!isMobile || isPageMobile(pages.data, t.page))),
  );
  if (visible.length < 2) return null;
  return (
    <div className="mb-4 flex gap-1 border-b border-border">
      {visible.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end
          className={({ isActive }) =>
            isActive
              ? 'border-b-2 border-tres-action px-3 py-1.5 text-sm font-medium text-tres-text-primary'
              : 'border-b-2 border-transparent px-3 py-1.5 text-sm text-tres-text-secondary hover:text-tres-text-primary'
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}
